import { Component, createRef } from 'preact';
import { cn } from '../lib/utils';
import { requestLoginCode, verifyLoginCode, setStoredToken, recordServerStartedAt } from '../utils/authApi';
import BetaBadge from './BetaBadge';
import ForgedButton from './ui/ForgedButton';
import { HeadingWatermark, BG_ATMOSPHERE, VIGNETTE, TEXT_PRIMARY, TEXT_BODY } from '../lib/medievalTheme';
import { t } from '../localization/t';

const DIGITS = 6;
const ENTER = 'animate-in fade-in-0 slide-in-from-bottom-2 fill-mode-backwards duration-500';
const ENTER_SHORT = 'animate-in fade-in-0 fill-mode-backwards duration-300';

// Solid recessed well; the same plate under the email line and each code tile.
function wellStyle(active) {
  return {
    background: active ? '#17110a' : '#0d0a06',
    border: `1px solid ${active ? '#b48c3c' : '#3d3019'}`,
    color: TEXT_PRIMARY,
    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.35)',
  };
}

// Step machine:
//   email → enter email; submitting sends a magic code.
//   code  → 6-digit magic-code verification.
// Signups are open to anyone.
export default class LoginScreen extends Component {
  constructor(props) {
    super(props);
    this.state = {
      step: 'email',
      email: '',
      code: '',
      error: null,
      loading: false,
      emailFocused: false,
      codeFocused: false,
    };
    this.emailInputRef = createRef();
    this.codeInputRef = createRef();
    this.startedAtPoll = null;
  }

  componentDidMount() {
    this.focusCurrentStep();
    // The /auth/capacity probe carries the server's startedAt timestamp,
    // which feeds maintenanceState's redeploy-detection. Poll it on the
    // login screen so a tester sitting on the login screen during a
    // server restart still observes the transition.
    recordServerStartedAt();
    this.startedAtPoll = setInterval(recordServerStartedAt, 30_000);
  }

  componentWillUnmount() {
    if (this.startedAtPoll) clearInterval(this.startedAtPoll);
  }

  componentDidUpdate(_prevProps, prevState) {
    if (prevState.step === this.state.step) return;
    this.focusCurrentStep();
  }

  focusCurrentStep() {
    const ref = this.state.step === 'email' ? this.emailInputRef : this.codeInputRef;
    const el = ref?.current;
    if (!el) return;
    el.focus();
    const len = (el.value || '').length;
    try { el.setSelectionRange(len, len); } catch (_) { /* number-type etc. */ }
  }

  handleEmailSubmit = async (e) => {
    e.preventDefault();
    const { email } = this.state;
    if (!email.trim()) return;

    this.setState({ loading: true, error: null });
    try {
      const result = await requestLoginCode(email.trim());
      // Dev auto-login: server bypasses the magic-code flow for
      // allowlisted emails on local channel. Treat the response as if
      // /auth/verify had returned it.
      if (result?.autoLogin && result.token) {
        await setStoredToken(result.token);
        this.props.onLogin(result);
        return;
      }
      this.setState({ step: 'code', loading: false });
    } catch (err) {
      this.setState({ error: err.message, loading: false });
    }
  };

  handleCodeSubmit = async (e) => {
    e.preventDefault();
    const { email, code } = this.state;
    if (!code.trim()) return;

    this.setState({ loading: true, error: null });
    try {
      const result = await verifyLoginCode(email.trim(), code.trim());
      await setStoredToken(result.token);
      this.props.onLogin(result);
    } catch (err) {
      this.setState({ error: err.message, loading: false });
    }
  };

  handleCodeInput = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, DIGITS);
    this.setState({ code: value });
  };

  resetToEmail = () => {
    this.setState({
      step: 'email',
      code: '',
      error: null,
      loading: false,
    });
  };

  render() {
    const { step, email, code, error, loading, codeFocused } = this.state;
    const { serverUnreachable } = this.props;
    const onCode = step === 'code';
    const canSubmit = onCode ? code.length === DIGITS : !!email.trim();
    const activeTile = Math.min(code.length, DIGITS - 1);

    return (
      <div
        className="fixed inset-0 z-50 flex flex-col items-center justify-center"
        style={{ background: `url("/tex-noise.webp"), ${BG_ATMOSPHERE}` }}
      >
        <HeadingWatermark opacity={0.1} hanging />
        <div className="fixed inset-0 pointer-events-none" style={{ background: VIGNETTE }} />
        <div className={cn('relative w-[48cqw] max-w-[90cqw] mb-6', ENTER)}>
          <img src="/valkenhall-logo.png" alt="Valkenhall" className="w-full" draggable={false} />
          <BetaBadge scale={2} />
        </div>

        {serverUnreachable ? (
          <div
            className="relative mt-4 mb-2 w-full max-w-[24cqw] rounded-xl px-4 py-3 text-sm"
            style={{ background: '#2a1c10', border: '1px solid #8a5a2a', color: '#e8c28a' }}
            role="status"
            aria-live="polite"
          >
            <div className="flex items-start gap-2">
              <span aria-hidden="true" className="mt-0.5">⚠</span>
              <div>
                <div className="font-semibold">{t('Valkenhall servers are unreachable')}</div>
                <div className="mt-1 text-xs" style={{ color: '#d4b789' }}>
                  {t("We'll keep retrying in the background. You can still try to sign in. It will work as soon as the connection is back.")}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* One row, rewritten in place: the label and the field swap where
            they stand, so the logo and button never move between steps. */}
        <form
          onSubmit={onCode ? this.handleCodeSubmit : this.handleEmailSubmit}
          className={cn('relative w-full max-w-[24cqw] mt-6 flex flex-col gap-4', ENTER)}
          style={{ animationDelay: '180ms' }}
        >
          <div className="flex flex-col gap-2">
            <label className="block text-sm" style={{ color: TEXT_BODY }}>
              <span key={step} className={cn('block', ENTER_SHORT)}>
                {onCode ? t('Login code') : t('Email address')}
              </span>
            </label>

            {onCode ? (
              <div className="relative">
                {/* One real input over the whole row keeps paste, one-time-code
                    autofill and backspace native; the tiles are display only. */}
                <input
                  ref={this.codeInputRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={DIGITS}
                  value={code}
                  className="no-smooth-caret absolute inset-0 z-10 w-full h-full opacity-0"
                  onInput={this.handleCodeInput}
                  onFocus={() => this.setState({ codeFocused: true })}
                  onBlur={() => this.setState({ codeFocused: false })}
                  disabled={loading}
                />
                <div aria-hidden="true" className="flex gap-2">
                  {Array.from({ length: DIGITS }, (_, i) => {
                    const here = codeFocused && i === activeTile;
                    return (
                      <div
                        key={i}
                        className={cn(
                          'flex-1 h-12 flex items-center justify-center rounded-lg text-2xl tabular-nums transition-colors duration-150',
                          ENTER_SHORT
                        )}
                        style={{ ...wellStyle(here), animationDelay: `${i * 40}ms` }}
                      >
                        {code[i] ?? (here ? <span className="login-code-caret h-6" /> : '')}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <input
                ref={this.emailInputRef}
                // `text`, not `email`: Chromium hides selectionStart on email
                // inputs, so the smooth-caret overlay can't track the cursor.
                type="text"
                inputMode="email"
                autoComplete="email"
                pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                value={email}
                placeholder="you@example.com"
                className={cn('w-full h-12 rounded-lg px-4 outline-none placeholder:text-stone-500 transition-colors duration-150', ENTER_SHORT)}
                style={wellStyle(this.state.emailFocused)}
                onInput={(e) => this.setState({ email: e.target.value, error: null })}
                onFocus={() => this.setState({ emailFocused: true })}
                onBlur={() => this.setState({ emailFocused: false })}
                disabled={loading}
              />
            )}
          </div>

          {error ? <p className="text-sm" style={{ color: '#c45050' }}>{error}</p> : null}

          <ForgedButton
            type="submit"
            tone="gold"
            radius="var(--radius-lg)"
            disabled={loading || !canSubmit}
            className="w-full py-3 text-sm font-semibold arena-heading"
          >
            <span key={`${step}-${loading}`} className={cn('block', ENTER_SHORT)}>
              {loading ? (onCode ? t('Verifying…') : t('Sending…')) : onCode ? t('Sign in') : t('Send login code')}
            </span>
          </ForgedButton>

          {/* Footer height is reserved on the first step so the centred
              column does not jump when the code step adds its lines. */}
          <div className="min-h-12 flex flex-col items-center gap-1 text-xs">
            {onCode ? (
              <>
                <span className={ENTER_SHORT} style={{ color: TEXT_BODY }}>
                  {t('Code sent to')} <span style={{ color: TEXT_PRIMARY }}>{email}</span>
                </span>
                <button
                  type="button"
                  className={cn('login-footer-link', ENTER_SHORT)}
                  onClick={this.resetToEmail}
                  disabled={loading}
                >
                  {t('Use a different email')}
                </button>
              </>
            ) : null}
          </div>
        </form>
      </div>
    );
  }
}
